import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { isInsideEditor } from '@dotcms/client';
import { Actions, OnInitEffects, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store, select } from '@ngrx/store';
import { combineLatestWith, delay, filter, map, mergeMap, switchMap, takeWhile } from 'rxjs/operators';

import { applyConfiguration } from 'ish-core/store/core/configuration';
import { isServerConfigurationLoaded } from 'ish-core/store/core/server-config';
import { getLoggedInUser, loginUserSuccess } from 'ish-core/store/customer/user';
import { ApiTokenService } from 'ish-core/utils/api-token/api-token.service';
import { DomService } from 'ish-core/utils/dom/dom.service';
import { mapErrorToAction, mapToPayloadProperty, whenTruthy } from 'ish-core/utils/operators';

import { DotCmsConfigurationHelper } from '../../models/configuration/dot-cms-configuration.helper';
import { CustomDotCmsPageHelper } from '../../models/page/custom-dot-cms-page.helper';
import { DotCmsResourceService } from '../../services/dot-cms-resource/dot-cms-resource.service';
import { getDotCmsHostIdAndIcmCurrentLocale } from '../utils/dotcms-store-utils';

import {
  allowDotCmsApiCall,
  getDotCmsSiteConfiguration,
  getDotCmsSiteConfigurationFail,
  loadDotCmsAvailableLanguagesSuccess,
  loadDotCmsConfiguration,
  loadDotCmsConfigurationSuccess,
  restrictDotCmsApiCall,
  setDotCmsApiCallRestrictionStatus,
  setDotCmsLanguagePreference,
  setDotCmsPersona,
  setDotCmsSelectedLanguage,
  updateDotCmsPersona,
} from './dotcms-configuration.action';
import {
  getCurrentDotCmsApiConfiguration,
  getCurrentDotCmsPersona,
  getDotCmsAvailableLanguages,
} from './dotcms-configuration.selectors';

@Injectable()
// eslint-disable-next-line ish-custom-rules/project-structure
export class DotCmsConfigurationEffects implements OnInitEffects {
  constructor(
    private actions$: Actions,
    private store: Store,
    private resourceService: DotCmsResourceService,
    private domService: DomService,
    private apiTokenService: ApiTokenService,
    @Inject(DOCUMENT) private document: Document
  ) {}

  loadDotCmsConfiguration$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadDotCmsConfiguration),
      switchMap(() => this.store.pipe(select(isServerConfigurationLoaded))),
      whenTruthy(),
      concatLatestFrom(() => this.store.pipe(select(getCurrentDotCmsApiConfiguration))),
      whenTruthy(),
      map(([, apiConfiguration]) => {
        this.setDotCmsStyleSheet(apiConfiguration.host, apiConfiguration.hostId);
        return loadDotCmsConfigurationSuccess({ apiConfiguration });
      })
    )
  );

  setApiCallRestrictionStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setDotCmsApiCallRestrictionStatus),
      takeWhile(() => !SSR),
      combineLatestWith(
        this.store.pipe(select(isServerConfigurationLoaded), whenTruthy()),
        this.store.pipe(select(getLoggedInUser))
      ),
      filter(([, serverConfig, user]) =>
        !SSR && this.apiTokenService.hasUserApiTokenCookie() ? !!user && !!serverConfig : !!serverConfig
      ),
      delay(100),
      map(() => allowDotCmsApiCall())
    )
  );

  restrictApiCall$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loginUserSuccess),
      map(() => restrictDotCmsApiCall())
    )
  );

  getDotCmsConfiguration$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getDotCmsSiteConfiguration),
      getDotCmsHostIdAndIcmCurrentLocale(this.store),
      mergeMap(([, hostId, icmCurrentLocale]) =>
        this.resourceService.getConfiguration({ hostId }).pipe(
          switchMap(configuration => [
            setDotCmsApiCallRestrictionStatus(),
            loadDotCmsAvailableLanguagesSuccess({ languages: configuration.languages }),
            setDotCmsSelectedLanguage({
              language: DotCmsConfigurationHelper.getSelectedLanguageItem(icmCurrentLocale, configuration.languages),
            }),
            updateDotCmsPersona(),
          ]),
          mapErrorToAction(getDotCmsSiteConfigurationFail)
        )
      )
    )
  );

  setDotCmsPersona$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateDotCmsPersona),
      takeWhile(() => !SSR),
      combineLatestWith(
        this.store.pipe(select(getCurrentDotCmsPersona)),
        this.store.pipe(select(isServerConfigurationLoaded), whenTruthy()),
        this.store.pipe(select(getLoggedInUser))
      ),
      delay(50),
      map(([, personaId]) => setDotCmsPersona({ personaId }))
    )
  );

  setDotCmsLanguagePreference = createEffect(() =>
    this.actions$.pipe(
      ofType(setDotCmsLanguagePreference),
      mapToPayloadProperty('language'),
      takeWhile(() => SSR || isInsideEditor()),
      combineLatestWith(this.store.pipe(select(getDotCmsAvailableLanguages), whenTruthy())),
      filter(([language, availableLanguages]) => !!language && !!availableLanguages?.length),
      map(([language, availableLanguages]) =>
        applyConfiguration({
          lang: CustomDotCmsPageHelper.getCurrentLocale(
            DotCmsConfigurationHelper.getDotcmsSelectedLanguageItem(language, availableLanguages)
          ),
        })
      )
    )
  );

  ngrxOnInitEffects(): Action {
    this.store.dispatch(loadDotCmsConfiguration());
    return getDotCmsSiteConfiguration();
  }

  private setDotCmsStyleSheet(host: string, hostId: string) {
    const existingLink = this.document.querySelector<HTMLLinkElement>(
      `link[href^="${host}/application/styles.dotsass?host_id=${hostId}"]`
    );
    if (!existingLink) {
      this.apiTokenService.hasUserApiTokenCookie();
      const link = this.domService.createElement<HTMLLinkElement>('link', this.document.head);
      this.domService.setProperty(link, 'rel', 'stylesheet');
      this.domService.setProperty(link, 'type', 'text/css');
      this.domService.setProperty(link, 'href', `${host}/application/styles.dotsass?host_id=${hostId}`);
    }
  }
}
